import { Form } from "antd";
import { useQuery } from "@apollo/client";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QuestionsQuery } from "../../types/survey";
import { GET_QUESTIONS } from "./survey.gql";
import { QUESTIONS, SURVEY_DONE } from "../../constants/storage";
import { appRoutes } from "../../constants/routes";
import useLocalStorage from "../../hooks/useLocalStorage";

const useSurvey = () => {
  const { setStorageItem } = useLocalStorage();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const {
    data,
    loading,
    error,
  } = useQuery<QuestionsQuery>(GET_QUESTIONS);
  const [steps, setSteps] = useState<string[]>([]);
  const currentStep = useMemo(() => steps.at(-1), [steps]) as string;
  const dataValues = Form.useWatch([], form);
  const initialValues = JSON.parse(localStorage.getItem(QUESTIONS) ?? "{}");
  // @TODO: clear storage on registration

  useEffect(() => {
    if (!data) return;

    setSteps((prevState) => {
      const firstQuestionId = data.questions[0].id;

      return prevState.includes(firstQuestionId) ? prevState : [...prevState, firstQuestionId];
    });
  }, [data]);

  const currentQuestion = useMemo(() => {
    return data?.questions.find(item => item.id === steps[steps.length - 1]);
  }, [steps]);

  const totalSteps = useMemo(() => {
    return data?.questions.reduce((result: any[], item) => {
      if (item.id === result.at(-1)) {
        const value = JSON.parse(item.next_question);

        if (value && typeof value === "object") {
          result.push(Object.values(value).at(-1)?.toString());
        } else if (item.next_question !== null) {
          result.push(item.next_question.toString());
        }
      }

      return result;
    }, [...steps]).length;
  }, [steps]) as number;

  const isNextStepDisabled = useMemo(() => {
    if (!currentQuestion) return true;

    const value = form.getFieldsValue()[currentQuestion.id];

    return value === undefined || value?.length === 0;
  }, [dataValues, currentStep]);

  const handleNextStep = () => {
    if (!data) return;

    const question = data.questions?.find(
      (item) => item.id === `${currentStep}`
    )?.next_question;

    localStorage.setItem("questions", JSON.stringify(steps.reduce(
      (result, value) => ({ ...result, [value]: form.getFieldsValue()[value] }),
      {}
    )));

    if (question === null) {
      setStorageItem(SURVEY_DONE, true);
      navigate(appRoutes.matchingProcess);
    } else {
      const next = JSON.parse(question ?? "{}");
      const nextStep = typeof next === "object" ? `${next[dataValues[currentStep]]}` : `${next}`;
      setSteps(prevState => [...prevState, nextStep]);
    }
  };

  const handlePrevStep = () => {
    setSteps((prevState) => prevState.slice(0, -1));
  };

  const percent = useMemo(() => {
    if (data) {
      const position = currentQuestion ? currentQuestion.position : data.questions[0].position;

      return (position / (totalSteps + 1)) * 100;
    }

    return 0;
  }, [currentQuestion, data]);

  return {
    data,
    loading,
    error,
    percent,
    steps,
    form,
    handleNextStep,
    handlePrevStep,
    isNextStepDisabled,
    currentStep,
    initialValues
  };
};

export default useSurvey;
